import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { AlertDto } from 'src/app/models/alerts/alerts.model';
import { KNOWLEDGE_ALERTS_FEATURE_CODE } from 'src/app/features/plans/models/entitlement.model';
import { PlanFeatureService } from 'src/app/features/plans/services/plan-feature.service';
import { AlertsService } from 'src/app/services/alerts/alerts.service';
import { LanguageService } from 'src/app/services/language/language.service';
import { FrontendLoggerService } from 'src/app/services/frontend-logger.service';

/**
 * R6 · Home IA — widget READ-ONLY "Requiere tu atención": top-5 de alertas de conocimiento
 * activas con deep-link a /alerts. Se auto-gatea por la feature `knowledge_alerts` (si el plan no
 * la tiene, el widget no se muestra). Degrada de forma independiente (loading/empty/error) sin
 * romper el resto del Home. Nunca renderiza evidencia completa: solo título/severidad/estado.
 */
@Component({
  selector: 'app-home-alerts-widget',
  templateUrl: './home-alerts-widget.component.html',
  styleUrls: ['./home-alerts-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class HomeAlertsWidgetComponent implements OnInit {
  featureAvailable = false;
  alerts: AlertDto[] = [];
  loading = false;
  error = false;

  readonly alertsLink = '/alerts';
  private readonly topN = 5;

  constructor(
    private readonly planFeatures: PlanFeatureService,
    private readonly alertsService: AlertsService,
    public readonly lang: LanguageService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly frontendLogger: FrontendLoggerService
  ) {}

  ngOnInit(): void {
    this.planFeatures.hasFeature(KNOWLEDGE_ALERTS_FEATURE_CODE).subscribe((enabled) => {
      this.featureAvailable = enabled;
      this.cdRef.markForCheck();
      if (enabled) {
        this.load();
      }
    });
  }

  translate(key: string): string {
    return this.lang.translate(key);
  }

  get isEmpty(): boolean {
    return !this.loading && !this.error && this.alerts.length === 0;
  }

  load(): void {
    this.loading = true;
    this.error = false;
    this.alertsService
      .getAlerts({ status: 'detected', page: 1, pageSize: this.topN })
      .pipe(finalize(() => {
        this.loading = false;
        this.cdRef.markForCheck();
      }))
      .subscribe({
        next: (page) => (this.alerts = (page.items ?? []).slice(0, this.topN)),
        error: (err) => {
          this.frontendLogger.error('[HomeAlertsWidget] load failed', err);
          this.error = true;
          this.alerts = [];
        },
      });
  }
}
